import React, { useCallback, useEffect } from 'react';
import { NextPage } from 'next';
import { useTranslations } from '@hooks/useTranslations';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { useGTM } from '@hooks/useGTM';
import Main from '@components/Layout/Main';
import LoginBrandId from '@components/MyAccount';
import routes from '../../constants/routes';
import {
    BRANDID_CLIENT_ID,
    BRANDID_LOGIN_URL,
    BRANDID_REGISTER_OFFERS_RETURN_URL,
    BRANDID_REDIRECT_BOUNCE_URL,
    BRANDID_REGISTER_URL,
    BRANDID_RETURN_URL,
    BRANDID_OFFERS_RETURN_URL,
} from '../../constants/myAccount';
import { ERROR_CODE } from '../_error';
import CustomError from '@components/CustomError';
import FAQs from '@components/FAQs';
import { batch, useSelector } from 'react-redux';
import { FilterDuck } from '../../redux/filters/filter.duck';
import CarDetailsDuck from '../../redux/carDetails/carDetails.duck';
import carDetailsDuck from '../../redux/carDetails/carDetails.duck';
import { brandIdRedirect } from '@utils/url.utils';
import { FEATURES_LIST } from '../../context/featureSwitchApp';
import { SESSION_COOKIE_NAME } from '../../constants/main';
import { getSessionIdCookie } from '@utils/Session.utils';
import { handleFinanceQuote, updateGeneralContext } from '@utils/getInitialProps/common';
import { DealService } from '../../services';
import { NeedHelpSection } from '@components/NeedHelpSection';
import { ContextWithStore } from '@utils/getInitialProps/@types';

const textStyle = {
    textAlign: 'center' as const,
    marginTop: '100px',
    marginBottom: '30px',
    fontWeight: 'bold' as const,
};

export const BASKET_OFFERS_MODAL_QUERY = 'withOffersModal';

const Login: NextPage = () => {
    const { t } = useTranslations();
    const { pushConnectionPagePageLoad } = useGTM();
    const isGigyaEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_GIGYA_ENABLED);
    const redirectBounce = BRANDID_REDIRECT_BOUNCE_URL;
    const paymentJourney = useSelector((state) => FilterDuck.getPaymentJourneyType(state));
    const isDealerLayerEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_DEALER_LAYER_ENABLED);
    const shouldDisplayGetPersonalisedOffer =
        useSelector((state) => CarDetailsDuck.getCurrentDealDealerLayerEnabled(state)) && isDealerLayerEnabled;

    const redirectTo = `${BRANDID_OFFERS_RETURN_URL}${routes.BASKET}?${BASKET_OFFERS_MODAL_QUERY}=true`;

    const brandIdRedirectRegistration = useCallback(() => {
        const brandIdStateJson: string = JSON.stringify({ redirectTo: BRANDID_REGISTER_OFFERS_RETURN_URL });
        const url = BRANDID_REGISTER_URL.replace('{redirectURL}', BRANDID_RETURN_URL);
        brandIdRedirect(url, brandIdStateJson, {
            isLogin: false,
        });
    }, []);

    useEffect(() => {
        pushConnectionPagePageLoad(paymentJourney);
    }, []);

    const brandIdRedirectLogin = useCallback(() => {
        const brandIdState: { redirectTo: string; redirectBounce?: string } = { redirectTo };

        // Add redirect bounce to state if its defined
        if (redirectBounce) {
            brandIdState.redirectBounce = redirectBounce;
        }

        const brandIdStateJson: string = JSON.stringify(brandIdState);

        const url = BRANDID_LOGIN_URL.replace('{clientId}', BRANDID_CLIENT_ID).replace(
            '{redirectURL}',
            BRANDID_RETURN_URL
        );
        brandIdRedirect(url, brandIdStateJson, {
            isLogin: true,
            isGigyaEnabled,
        });
    }, []);

    if (!shouldDisplayGetPersonalisedOffer) {
        return (
            <Main title={String(ERROR_CODE.NOT_FOUND)}>
                <CustomError
                    errorType={String(ERROR_CODE.NOT_FOUND)}
                    returnLink={routes.ROOT}
                    returnLinkLabel={t('errorPage.returnLink.label')}
                    titleDescription={t('errorPage.notFound.description')}
                />
                <NeedHelpSection />
                <FAQs />
            </Main>
        );
    }

    return (
        //TODO consider offers login title
        <Main title={t('checkout.tabTitle')} checkout>
            <div style={textStyle}>{t('connectionForOffer.text')}</div>
            <LoginBrandId
                brandIdRedirectRegistration={brandIdRedirectRegistration}
                brandIdRedirectLogin={brandIdRedirectLogin}
            />
        </Main>
    );
};

Login.getInitialProps = async (ctx: ContextWithStore) => {
    const { store, query, req } = await updateGeneralContext(ctx);
    try {
        const token = req?.cookies?.[SESSION_COOKIE_NAME] ?? getSessionIdCookie();
        if (!CarDetailsDuck.getCurrentDeal(store.getState())?.token) {
            const currentDeal = await DealService.currentDeal(token, false, false);

            batch(() => {
                store.dispatch(CarDetailsDuck.setCurrentCarDetails(currentDeal.data.fullProductConfiguration));
                store.dispatch(carDetailsDuck.setCurrentDeal(currentDeal.data));
            });
            await handleFinanceQuote(store, currentDeal.data.financeQuoteId);
        }
    } catch (e: any) {
        console.error(e);
    }
    return { query, namespacesRequired: ['common'] };
};

export default Login;
